/**
 * Push Notification Reminder - Shows toast reminder to enable push notifications
 * Displays on every PWA open if notifications are not enabled
 * @module components/PushNotificationReminder
 * @version 4.10.1
 */

import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { isInstalledPWA } from '../utils/deviceUtils';

const REMINDER_SHOWN_KEY = 'push-reminder-shown';

export const PushNotificationReminder: React.FC = () => {
  const { isSupported, permission, isSubscribed, subscribe, isLoading } = usePushNotifications();
  const [dismissed, setDismissed] = useState(false);
  const [showReminder, setShowReminder] = useState(false);

  useEffect(() => {
    // Only show in PWA context
    if (!isInstalledPWA()) {
      return;
    }

    // Only if push is supported but not granted/subscribed
    if (!isSupported) {
      return;
    }

    // Already subscribed, no need to show
    if (permission === 'granted' && isSubscribed) {
      return;
    }

    // User explicitly denied, don't nag
    if (permission === 'denied') {
      return;
    }

    // Check session storage to avoid showing multiple times per session
    const reminderShown = sessionStorage.getItem(REMINDER_SHOWN_KEY);
    if (reminderShown) {
      return;
    }

    // Show reminder after a short delay (let dashboard load first)
    const timer = setTimeout(() => {
      setShowReminder(true);
      sessionStorage.setItem(REMINDER_SHOWN_KEY, 'true');
    }, 3000);

    return () => clearTimeout(timer);
  }, [isSupported, permission, isSubscribed]);

  const handleEnable = async () => {
    const success = await subscribe();
    if (success) {
      setShowReminder(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowReminder(false);
    // Will show again next session (sessionStorage clears on close)
  };

  if (!showReminder || dismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-2xl shadow-2xl border border-amber-200 p-4 z-40 animate-in slide-in-from-bottom-4">
      {/* Close button */}
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
        aria-label="Cerrar"
      >
        <X size={18} />
      </button>

      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
          <Bell size={24} className="text-white" />
        </div>

        {/* Content */}
        <div className="flex-1 pr-4">
          <h3 className="font-bold text-gray-900 mb-1">
            Activa las notificaciones
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Recibi alertas de partidos, pagos y mensajes importantes de La Liga.
          </p>

          {/* Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleEnable}
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-2 px-4 rounded-lg text-sm hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 transition-all"
            >
              {isLoading ? 'Activando...' : 'Activar'}
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm hover:bg-gray-100 rounded-lg transition-colors"
            >
              Mas tarde
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationReminder;
